import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Eye,
  AlertCircle,
  CheckCircle,
  Clock,
  Trash2,
  Building2,
  MapPin,
  Users,
  FileText,
  BarChart3,
  LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  formatCurrency,
  formatDate,
  ROUTE_PATHS,
  type Analisi,
  type Documento,
  type Condominio,
  type AnalisiKPI,
} from '@/lib/index';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: LucideIcon;
  trend?: number;
  trendValue?: string;
}

export const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  trendValue,
}: StatCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 35 }}
      whileHover={{ scale: 1.02 }}
    >
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-lg border-l-4 border-l-primary">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm font-medium text-muted-foreground mb-1">
                {title}
              </p>
              <p className="text-3xl font-semibold tracking-tight">{value}</p>
              {subtitle && (
                <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
              )}
              {trend !== undefined && (
                <div className="flex items-center gap-1 mt-2">
                  {trend > 0 ? (
                    <TrendingUp className="h-4 w-4 text-success" />
                  ) : trend < 0 ? (
                    <TrendingDown className="h-4 w-4 text-danger" />
                  ) : null}
                  <span
                    className={`text-sm font-medium ${
                      trend > 0
                        ? 'text-success'
                        : trend < 0
                        ? 'text-danger'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {trendValue || `${trend > 0 ? '+' : ''}${trend.toFixed(1)}%`}
                  </span>
                </div>
              )}
            </div>
            <div className="p-3 rounded-xl bg-primary/10 text-primary transition-colors">
              <Icon className="h-6 w-6" />
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

interface AnalisiCardProps {
  analisi: Analisi;
  onView?: () => void;
  onDelete?: () => void;
}

export const AnalisiCard = ({ analisi, onView, onDelete }: AnalisiCardProps) => {
  const statoConfig: Record<
    string,
    {
      label: string;
      variant: 'default' | 'secondary' | 'destructive' | 'outline';
      icon: typeof Clock;
      className?: string;
    }
  > = {
    bozza: { label: 'Bozza', variant: 'secondary', icon: Clock },
    elaborazione: {
      label: 'Elaborazione...',
      variant: 'outline',
      icon: Clock,
      className: 'animate-pulse',
    },
    completata: { label: 'Completata', variant: 'default', icon: CheckCircle },
    errore: { label: 'Errore', variant: 'destructive', icon: AlertCircle },
  };

  const stato = statoConfig[analisi.stato] || statoConfig['bozza'];
  const StatoIcon = stato.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 35 }}
      whileHover={{ scale: 1.02 }}
    >
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg font-semibold mb-2">
                {analisi.titolo}
              </CardTitle>
              {analisi.nome_condominio && (
                <p className="text-sm text-muted-foreground mb-1">
                  {analisi.nome_condominio}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {formatDate(analisi.created_at)}
              </p>
            </div>
            <Badge
              variant={stato.variant}
              className={`flex items-center gap-1 ${stato.className || ''}`}
            >
              {analisi.stato === 'elaborazione' ? (
                <Clock className="h-3 w-3 animate-spin" />
              ) : (
                <StatoIcon className="h-3 w-3" />
              )}
              {stato.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-sm">
            <p className="text-muted-foreground mb-1">Totale Uscite</p>
            <p className="text-lg font-semibold">
              {formatCurrency(analisi.totale_uscite)}
            </p>
          </div>
          {analisi.stato === 'completata' && (
            <div className="flex flex-wrap gap-2 pt-2 border-t">
              {onView ? (
                <Button variant="default" size="sm" className="flex-1" onClick={onView}>
                  <Eye className="h-4 w-4 mr-1" />
                  Visualizza
                </Button>
              ) : (
                <Button variant="default" size="sm" className="flex-1" asChild>
                  <Link to={`${ROUTE_PATHS.ANALISI}/${analisi.id}`}>
                    <Eye className="h-4 w-4 mr-1" />
                    Visualizza
                  </Link>
                </Button>
              )}
              {analisi.condominio_id && (
                <Button variant="outline" size="sm" className="gap-1" asChild>
                  <Link to={`/condomini/${analisi.condominio_id}/dashboard`}>
                    <BarChart3 className="h-4 w-4" />
                    Dashboard
                  </Link>
                </Button>
              )}
              {onDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDelete}
                  className="text-danger hover:bg-danger/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

interface CondominioCardProps {
  condominio: Condominio;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const CondominioCard = ({
  condominio,
  onEdit,
  onDelete,
}: CondominioCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 35 }}
      whileHover={{ scale: 1.02 }}
    >
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold">
                  {condominio.nome}
                </CardTitle>
                {condominio.indirizzo && (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                    <MapPin className="h-3 w-3" />
                    <span>{condominio.indirizzo}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {condominio.amministratore && (
            <div className="text-sm">
              <p className="text-muted-foreground mb-1">Amministratore</p>
              <p className="font-medium">{condominio.amministratore}</p>
            </div>
          )}
          {condominio.num_unita && (
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Unità:</span>
              <span className="font-semibold">{condominio.num_unita}</span>
            </div>
          )}
          <div className="flex gap-2 pt-2 border-t flex-wrap">
            <Button
              variant="default"
              size="sm"
              className="flex-1 gap-1"
              asChild
            >
              <Link to={`/condomini/${condominio.id}/dashboard`}>
                <BarChart3 className="h-4 w-4" />
                Dashboard
              </Link>
            </Button>
            {onEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
              >
                Modifica
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={onDelete}
                className="text-danger hover:bg-danger/10"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

interface DocumentoCardProps {
  documento: Documento;
  onAnalizza?: () => void;
  onDelete?: () => void;
}

export const DocumentoCard = ({
  documento,
  onAnalizza,
  onDelete,
}: DocumentoCardProps) => {
  const tipoConfig: Record<string, { label: string; variant: 'default' | 'secondary' }> = {
    consuntivo: { label: 'Consuntivo', variant: 'default' },
    preventivo: { label: 'Preventivo', variant: 'secondary' },
  };

  const statoConfig: Record<
    string,
    {
      label: string;
      variant: 'default' | 'secondary' | 'destructive' | 'outline';
      icon: typeof CheckCircle;
      className?: string;
    }
  > = {
    caricato: { label: 'Caricato', variant: 'secondary', icon: CheckCircle },
    elaborazione: {
      label: 'Elaborazione...',
      variant: 'outline',
      icon: Clock,
      className: 'animate-pulse',
    },
    analizzato: { label: 'Analizzato', variant: 'default', icon: CheckCircle },
    errore: { label: 'Errore', variant: 'destructive', icon: AlertCircle },
  };

  const tipo = tipoConfig[documento.tipo] || tipoConfig['consuntivo'];
  const stato = statoConfig[documento.stato] || statoConfig['caricato'];
  const StatoIcon = stato.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 35 }}
      whileHover={{ scale: 1.02 }}
    >
      <Card className="overflow-hidden transition-all duration-200 hover:shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={tipo.variant}>{tipo.label}</Badge>
                <Badge variant="outline">{documento.anno}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium truncate">{documento.nome_file}</p>
              </div>
              {documento.condominio_nome && (
                <p className="text-xs text-muted-foreground mt-1">
                  {documento.condominio_nome}
                </p>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Stato:</span>
            <Badge
              variant={stato.variant}
              className={`flex items-center gap-1 ${stato.className || ''}`}
            >
              {documento.stato === 'elaborazione' ? (
                <Clock className="h-3 w-3 animate-spin" />
              ) : (
                <StatoIcon className="h-3 w-3" />
              )}
              {stato.label}
            </Badge>
          </div>
          <div className="flex gap-2 pt-2 border-t">
            {onAnalizza && (
              <Button
                variant="default"
                size="sm"
                className="flex-1"
                onClick={onAnalizza}
                disabled={documento.stato === 'elaborazione'}
              >
                {documento.stato === 'elaborazione' ? (
                  <>
                    <Clock className="h-4 w-4 mr-1 animate-spin" />
                    Elaborazione...
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-1" />
                    {documento.stato === 'analizzato' ? 'Rianalizza' : 'Analizza'}
                  </>
                )}
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={onDelete}
                className="text-danger hover:bg-danger/10"
                disabled={documento.stato === 'elaborazione'}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

interface KPICardProps {
  kpi: AnalisiKPI;
}

export const KPICard = ({ kpi }: KPICardProps) => {
  const statoConfig = {
    ok: {
      borderColor: 'border-l-success',
      bgColor: 'bg-success/5',
      icon: CheckCircle,
      iconColor: 'text-success',
    },
    attenzione: {
      borderColor: 'border-l-warning',
      bgColor: 'bg-warning/5',
      icon: AlertCircle,
      iconColor: 'text-warning',
    },
    critico: {
      borderColor: 'border-l-danger',
      bgColor: 'bg-danger/5',
      icon: AlertCircle,
      iconColor: 'text-danger',
    },
  };

  const config = statoConfig[kpi.stato];
  const Icon = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 35 }}
    >
      <Card
        className={`overflow-hidden transition-all duration-200 hover:shadow-md border-l-4 ${config.borderColor} ${config.bgColor}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-4 w-4 ${config.iconColor}`} />
                <p className="text-sm font-medium text-muted-foreground">
                  {kpi.label || kpi.titolo}
                </p>
              </div>
              <p className="text-2xl font-bold tracking-tight">
                {kpi.valore}
                {(kpi.dettaglio || kpi.unita) && (
                  <span className="text-sm font-normal text-muted-foreground ml-1">
                    {kpi.dettaglio || kpi.unita}
                  </span>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};